import React, { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import { ListBullets, CircleNotch, ArrowRight, MagnifyingGlass } from "@phosphor-icons/react";

export const TemplateLayers = ({ onUsePrompt }) => {
  const [open, setOpen] = useState(false);
  const [files, setFiles] = useState([]);
  const [sel, setSel] = useState(null);
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState(null);

  const loadFiles = async () => {
    try {
      const m = await api.manuals();
      setFiles(m.filter((x) => !x.builtin && (x.category === "template" || x.category === "context")));
    } catch { toast.error("Could not load project files."); }
  };

  const inspect = async (f) => {
    setSel(f.id); setBusy(true); setData(null);
    const t = toast.loading(`Reading layers & styles from ${f.name}…`);
    try {
      const d = await api.inspectTemplate(f.id);
      setData(d);
      toast.success(`Found ${d.layers.length} layers, ${d.text_styles.length} text styles`, { id: t });
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not inspect the template.", { id: t }); }
    finally { setBusy(false); }
  };

  const useForGen = () => {
    if (!data) return;
    const parts = [];
    if (data.layers?.length) parts.push(`LAYERS (use these EXACT names, create only if absent): ${data.layers.join(", ")}`);
    if (data.text_styles?.length) parts.push(`TEXT STYLES: ${data.text_styles.join(", ")}`);
    if (data.dim_styles?.length) parts.push(`DIMENSION STYLES: ${data.dim_styles.join(", ")}`);
    const prompt =
      `Match the attached template "${data.name}" EXACTLY. Put all linework, text, monuments and improvements on the correct template layers, and reuse the template's text/dimension styles, fonts and sizes.\n\n${parts.join("\n")}\n\n(Describe the routine you want below — it must conform to the layers/styles above.)`;
    onUsePrompt(prompt);
    setOpen(false);
    toast.success("Template layers/styles added to the generator prompt");
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (o) loadFiles(); }}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm" data-testid="template-layers-btn"
          className="h-8 rounded-[3px] gap-1.5 text-xs font-mono text-[var(--text-dim)] hover:text-[var(--text)] hover:bg-[var(--surface-2)]">
          <ListBullets size={14} weight="bold" /> Template
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-[var(--surface)] border-[var(--border)] text-[var(--text)] max-w-2xl rounded-[4px]" data-testid="template-layers-dialog">
        <DialogHeader>
          <DialogTitle className="font-mono text-base flex items-center gap-2"><ListBullets size={18} weight="bold" className="text-[var(--primary)]" />Template Layers & Styles</DialogTitle>
          <DialogDescription className="text-[var(--text-dim)] text-xs">Read the exact layers, text styles and dimension styles from an uploaded template/finished CAD so generations match them.</DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-3">
          <div className="border border-[var(--border)] rounded-[3px]">
            <div className="px-2.5 py-1.5 text-[10px] font-mono uppercase tracking-wider text-[var(--text-dim)] bg-[var(--surface-2)] border-b border-[var(--border)]">Uploaded files</div>
            <ScrollArea className="h-[240px]">
              {files.length === 0 && <div className="p-3 text-[11px] text-[var(--text-dim)]">Upload a template / finished CAD in the left Project Files panel first.</div>}
              {files.map((f) => (
                <button key={f.id} onClick={() => inspect(f)} data-testid={`template-file-${f.id}`}
                  className={`w-full text-left px-2.5 py-2 border-b border-[var(--border)]/40 text-[12px] hover:bg-[var(--surface-2)] transition-colors ${sel === f.id ? "text-[var(--primary)]" : "text-[var(--text)]"}`}>
                  <div className="truncate flex items-center gap-1.5"><MagnifyingGlass size={12} />{f.name}</div>
                  <div className="text-[10px] font-mono text-[var(--text-dim)]">{f.type?.toUpperCase()} · {f.num_chunks} sections</div>
                </button>
              ))}
            </ScrollArea>
          </div>

          <div className="border border-[var(--border)] rounded-[3px] flex flex-col" data-testid="template-result">
            <div className="px-2.5 py-1.5 text-[10px] font-mono uppercase tracking-wider text-[var(--text-dim)] bg-[var(--surface-2)] border-b border-[var(--border)]">Detected standards</div>
            <ScrollArea className="h-[240px] p-2.5">
              {busy && <div className="flex items-center gap-2 text-[12px] text-[var(--text-dim)]"><CircleNotch size={14} className="animate-spin" />Reading…</div>}
              {!busy && !data && <div className="text-[11px] text-[var(--text-dim)]">Pick a file on the left to read its layers & styles.</div>}
              {!busy && data && (
                <div className="flex flex-col gap-3">
                  {[["Layers", data.layers], ["Text styles", data.text_styles], ["Dimension styles", data.dim_styles]].map(([label, arr]) => (
                    <div key={label}>
                      <div className="text-[10px] font-mono uppercase tracking-wider text-[var(--primary)] mb-1">{label} ({(arr || []).length})</div>
                      <div className="flex flex-wrap gap-1">
                        {(arr || []).length === 0 ? <span className="text-[11px] text-[var(--text-dim)]">— none detected</span>
                          : arr.map((n, i) => <span key={i} data-testid={`template-tag-${i}`} className="px-1.5 py-0.5 rounded-[2px] text-[10px] font-mono bg-[var(--surface-2)] border border-[var(--border)] text-[var(--text-dim)]">{n}</span>)}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </div>
        </div>

        {data && (
          <Button onClick={useForGen} data-testid="use-template-btn"
            className="h-9 rounded-[3px] gap-2 bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white">
            <ArrowRight size={15} /> Use these layers/styles to generate LISP
          </Button>
        )}
      </DialogContent>
    </Dialog>
  );
};
